// Kind → display copy for the admin telemetry surfaces (filter dropdowns,
// events explorer row labels, summary panel headers). Voice rules from
// prd/02_voice.md: terse, specific, no exclamations. Labels stay ≤ 40 chars so
// they fit a MultiSelect chip without truncation. Descriptions answer "what
// does this event mean" for an admin scanning the table.
//
// The satisfies clause enforces exhaustiveness — adding a kind to events.ts
// without an entry here becomes a compile error.

import {
  TELEMETRY_KINDS,
  type TelemetryEventKind,
  type TelemetrySurface,
} from './events.js';

export type TelemetryDisplay = {
  label: string;
  description: string;
  surface?: TelemetrySurface;
};

export const TELEMETRY_DISPLAY: Record<TelemetryEventKind, TelemetryDisplay> = {
  // ---- app lifecycle ----
  'session.started': {
    label: 'Session started',
    description: 'First authenticated load in a new browser tab.',
  },
  'route.changed': {
    label: 'Route changed',
    description: 'SPA navigation resolved to a new page.',
  },
  'web_vitals.reported': {
    label: 'Web Vitals reported',
    description: 'LCP / INP / CLS / TTFB / FCP sampled at page hide.',
  },
  'error.client': {
    label: 'Client error',
    description: 'window.onerror or unhandled promise rejection caught.',
  },
  'error.boundary': {
    label: 'Error boundary tripped',
    description: 'React error boundary caught a render-time exception.',
  },
  'error.api': {
    label: 'API error',
    description: 'fetchOk surfaced a non-2xx response to the SPA.',
    surface: 'other',
  },

  // ---- auth ----
  'auth.hat_switched': {
    label: 'Hat switched',
    description: 'User flipped between artist / host / admin context.',
    surface: 'auth',
  },
  'auth.signed_out': {
    label: 'Signed out',
    description: 'Session cleared from the SPA.',
    surface: 'auth',
  },
  'auth.password_changed': {
    label: 'Password changed',
    description: 'User completed an in-app password change.',
    surface: 'settings',
  },

  // ---- browse ----
  'browse.filter_applied': {
    label: 'Browse filter applied',
    description: 'A filter key was added or changed on the browse grid.',
    surface: 'browse',
  },
  'browse.filter_zero_results': {
    label: 'Browse filter zero results',
    description: 'The current filter combination matched no venues.',
    surface: 'browse',
  },
  'browse.map_view_toggled': {
    label: 'Browse map toggled',
    description: 'User switched between list and map views.',
    surface: 'browse',
  },
  'browse.map_bounds_changed': {
    label: 'Browse map panned',
    description: 'Map viewport changed; debounced bounds emit.',
    surface: 'browse',
  },
  'browse.card_clicked': {
    label: 'Browse card clicked',
    description: 'A venue card was opened from the browse grid.',
    surface: 'browse',
  },
  'browse.load_more_clicked': {
    label: 'Browse load more',
    description: 'Next page of venues was requested.',
    surface: 'browse',
  },
  'browse.smart_match_toggled': {
    label: 'Smart Match toggled',
    description: 'Artist toggled Smart Match on or off.',
    surface: 'browse',
  },

  // ---- detail pages ----
  'venue.detail_viewed': {
    label: 'Venue detail viewed',
    description: 'Venue profile page rendered.',
    surface: 'venue-detail',
  },
  'venue.send_request_clicked': {
    label: 'Send request clicked',
    description: 'Artist opened the request modal from a venue page.',
    surface: 'venue-detail',
  },
  'artist.detail_viewed': {
    label: 'Artist detail viewed',
    description: 'Artist profile page rendered.',
    surface: 'venue-detail',
  },
  'event.detail_viewed': {
    label: 'Event detail viewed',
    description: 'Event page rendered.',
    surface: 'event-detail',
  },

  // ---- events flow ----
  'event.interest_modal_opened': {
    label: 'Interest modal opened',
    description: 'Artist opened the submit-interest dialog.',
    surface: 'event-detail',
  },
  'event.interest_modal_closed': {
    label: 'Interest modal closed',
    description: 'Modal closed; submitted flag distinguishes abandon vs send.',
    surface: 'event-detail',
  },
  'event.interest_submitted': {
    label: 'Event interest submitted',
    description: 'Artist sent an interest to a host event.',
    surface: 'event-detail',
  },
  'event.interest_withdrawn': {
    label: 'Event interest withdrawn',
    description: 'Artist pulled an interest back before host action.',
    surface: 'event-detail',
  },

  // ---- request modal + thread ----
  'request.modal_opened': {
    label: 'Request modal opened',
    description: 'Artist opened the venue request dialog.',
    surface: 'venue-detail',
  },
  'request.modal_closed': {
    label: 'Request modal closed',
    description: 'Modal closed; submitted flag distinguishes abandon vs send.',
    surface: 'venue-detail',
  },
  'request.submitted': {
    label: 'Request submitted',
    description: 'Artist sent a stay request to a venue.',
    surface: 'venue-detail',
  },
  'inbox.thread_clicked': {
    label: 'Inbox thread clicked',
    description: 'User opened a thread from the inbox list.',
    surface: 'inbox',
  },
  'inbox.filter_chip_clicked': {
    label: 'Inbox filter chip clicked',
    description: 'Inbox filter chip toggled (all / unread / active / past).',
    surface: 'inbox',
  },
  'inbox.search_used': {
    label: 'Inbox search used',
    description: 'Debounced search query ran across thread previews.',
    surface: 'inbox',
  },
  'thread.message_sent': {
    label: 'Thread message sent',
    description: 'Outgoing message accepted by the worker.',
    surface: 'thread',
  },
  'thread.message_send_failed': {
    label: 'Thread message failed',
    description: 'Send was rejected — see reason in props.',
    surface: 'thread',
  },
  'thread.attachment_uploaded': {
    label: 'Attachment uploaded',
    description: 'One or more attachments attached to a message.',
    surface: 'thread',
  },
  'thread.attachment_upload_failed': {
    label: 'Attachment upload failed',
    description: 'R2 upload or size/type check rejected the file.',
    surface: 'thread',
  },

  // ---- dashboard ----
  'dashboard.viewed': {
    label: 'Dashboard viewed',
    description: 'Artist or host dashboard rendered.',
  },
  'dashboard.stat_clicked': {
    label: 'Dashboard stat clicked',
    description: 'A stat tile was opened for detail.',
  },
  'dashboard.up_next_action_clicked': {
    label: 'Up Next action clicked',
    description: 'CTA inside the Up Next card was taken.',
  },
  'dashboard.quick_action_clicked': {
    label: 'Quick action clicked',
    description: 'Row item in the quick-action strip was taken.',
  },

  // ---- forms ----
  'form.started': {
    label: 'Form started',
    description: 'Form mounted with telemetry attached.',
  },
  'form.field_validation_error': {
    label: 'Form validation error',
    description: 'A Zod rule rejected a field value.',
  },
  'form.abandoned': {
    label: 'Form abandoned',
    description: 'User navigated away with unsaved changes.',
  },
  'form.submitted': {
    label: 'Form submitted',
    description: 'Form passed client validation and posted.',
  },
  'form.submit_failed': {
    label: 'Form submit failed',
    description: 'Server rejected the submission.',
  },

  // ---- friction ----
  'empty_state.viewed': {
    label: 'Empty state viewed',
    description: 'A surface rendered its empty-state copy.',
  },
  'empty_state.cta_clicked': {
    label: 'Empty state CTA clicked',
    description: 'The CTA inside an empty state was taken.',
  },

  // ---- host-specific ----
  'host.address_lookup_used': {
    label: 'Address lookup used',
    description: 'Nominatim autocomplete was queried on a venue form.',
    surface: 'host-venue-create',
  },

  // ---- share ----
  'share.exchange_card_opened': {
    label: 'Share card opened',
    description: 'Post-completion share card surface rendered.',
    surface: 'exchange-detail',
  },
  'share.exchange_card_action': {
    label: 'Share card action',
    description: 'Native share, Twitter, or copy-link action taken.',
    surface: 'exchange-detail',
  },

  // ---- server-emitted ----
  'account.signed_up': {
    label: 'Account signed up',
    description: 'New user created an account.',
    surface: 'auth',
  },
  'account.signed_in': {
    label: 'Account signed in',
    description: 'Existing user authenticated.',
    surface: 'auth',
  },
  'exchange.drafted': {
    label: 'Exchange drafted',
    description: 'Artist saved a draft exchange before submitting.',
  },
  'exchange.submitted': {
    label: 'Exchange submitted',
    description: 'Artist sent the request to a host.',
  },
  'exchange.confirmed': {
    label: 'Exchange confirmed',
    description: 'Host confirmed an incoming request.',
  },
  'exchange.declined': {
    label: 'Exchange declined',
    description: 'Host declined an incoming request.',
  },
  'exchange.cancelled': {
    label: 'Exchange cancelled',
    description: 'Either party cancelled before the stay.',
  },
  'exchange.completed': {
    label: 'Exchange completed',
    description: 'Stay reached the completion sweep.',
  },
  'event.published': {
    label: 'Event published',
    description: 'Host moved an event from draft to published.',
    surface: 'host-event-manage',
  },
  'event_interest.submitted': {
    label: 'Event interest submitted',
    description: 'Server-side record of an artist interest.',
    surface: 'event-detail',
  },
  'event_interest.accepted': {
    label: 'Event interest accepted',
    description: 'Host accepted an interest; exchange spawned.',
    surface: 'host-event-manage',
  },
  'review.submitted': {
    label: 'Review submitted',
    description: 'One side filed a review for a completed exchange.',
  },
  'review.released': {
    label: 'Review released',
    description: 'Both reviews became visible after mutual release.',
  },
  'subscription.trial_started': {
    label: 'Trial started',
    description: 'Host entered the founding-cohort trial window.',
  },
  'subscription.trial_ended_first_charge': {
    label: 'First charge succeeded',
    description: 'Trial closed and the first invoice was paid.',
  },
  'verification.submitted': {
    label: 'Verification submitted',
    description: 'User uploaded documents for review.',
  },
  'verification.approved': {
    label: 'Verification approved',
    description: 'Admin marked the verification as approved.',
  },
  'share.viewed': {
    label: 'Share card viewed',
    description: 'Public share card endpoint served a card.',
    surface: 'exchange-detail',
  },
} satisfies Record<TelemetryEventKind, TelemetryDisplay>;

export function getTelemetryDisplay(kind: string): TelemetryDisplay {
  if (isKnownKind(kind)) return TELEMETRY_DISPLAY[kind];
  const tail = kind.split('.').slice(1).join('.') || kind;
  const label = tail
    .replace(/_/g, ' ')
    .replace(/\b\w/, (c) => c.toUpperCase());
  return {
    label: label || 'Unknown event',
    description: 'No display copy for this kind yet.',
  };
}

function isKnownKind(kind: string): kind is TelemetryEventKind {
  return (TELEMETRY_KINDS as readonly string[]).includes(kind);
}
